import { useState, type ChangeEvent } from 'react';
import { Button, Dialog, Field, Input, useToast } from '@/components/ui';
import { useDeliveryStatus } from '@/features/deliveries/api';
import type {
  DeliveryStatusInput,
  DeskStatus,
  StaffDelivery,
} from '@/features/deliveries/types';
import { isApiError } from '@/lib/api';
import styles from './dispensary.module.css';
import { NEXT_STEPS } from './delivery-format';
import { errorBody } from './outside-rx';

const COPY: Record<
  DeskStatus,
  { title: string; submit: string; done: string; danger?: boolean }
> = {
  out_for_delivery: {
    title: 'Out for delivery',
    submit: 'Hand to courier',
    done: 'Handed to the courier',
  },
  delivered: {
    title: 'Record delivery',
    submit: 'Mark delivered',
    done: 'Delivered',
  },
  failed: {
    title: 'Delivery failed',
    submit: 'Record failed attempt',
    done: 'Failed attempt recorded',
    danger: true,
  },
  returned: {
    title: 'Returned to the clinic',
    submit: 'Mark returned',
    done: 'Returned to the clinic',
  },
  cancelled: {
    title: 'Cancel delivery request',
    submit: 'Cancel delivery',
    done: 'Delivery cancelled',
    danger: true,
  },
};

const noop = () => {};

/** Codes where the delivery changed under us: the queue is reloaded. */
const STALE = ['VERSION_CONFLICT', 'DELIVERY_WRONG_STATUS'];

type Values = {
  courierName: string;
  courierPhone: string;
  trackingUrl: string;
  recipientName: string;
  idChecked: boolean;
  note: string;
  reason: string;
};
type Errors = Partial<Record<keyof Values, string>>;

function validate(step: DeskStatus, v: Values): Errors {
  const e: Errors = {};
  const len = (s: string) => s.trim().length;
  if (step === 'out_for_delivery') {
    if (
      len(v.courierName) &&
      (len(v.courierName) < 2 || len(v.courierName) > 80)
    )
      e.courierName = 'Courier name: 2 to 80 characters.';
    if (
      len(v.courierPhone) &&
      (len(v.courierPhone) < 6 || len(v.courierPhone) > 30)
    )
      e.courierPhone = 'Courier phone: 6 to 30 characters.';
    if (len(v.trackingUrl)) {
      let ok = false;
      try {
        ok = new URL(v.trackingUrl.trim()).protocol === 'https:';
      } catch {
        ok = false;
      }
      if (!ok || len(v.trackingUrl) > 500)
        e.trackingUrl = 'Tracking link: an https:// link.';
    }
  }
  if (step === 'delivered') {
    if (len(v.recipientName) < 2 || len(v.recipientName) > 120)
      e.recipientName = 'Enter who received it (2 to 120 characters).';
    if (len(v.note) > 300) e.note = 'Proof note: up to 300 characters.';
  }
  if (step === 'failed' || step === 'cancelled') {
    if (len(v.reason) < 3 || len(v.reason) > 300)
      e.reason = 'Enter the reason (3 to 300 characters).';
  }
  return e;
}

function body(
  step: DeskStatus,
  version: number,
  v: Values,
): DeliveryStatusInput {
  const opt = (s: string) => (s.trim() ? s.trim() : undefined);
  switch (step) {
    case 'out_for_delivery':
      return {
        status: step,
        version,
        courierName: opt(v.courierName),
        courierPhone: opt(v.courierPhone),
        trackingUrl: opt(v.trackingUrl),
      };
    case 'delivered':
      return {
        status: step,
        version,
        recipientName: v.recipientName.trim(),
        idChecked: v.idChecked,
        note: opt(v.note),
      };
    case 'failed':
    case 'cancelled':
      return { status: step, version, reason: v.reason.trim() };
    case 'returned':
      return { status: step, version };
  }
}

/**
 * RL-1186: one courier step on a delivery (POST
 * /dispensary/deliveries/{id}/status, versioned). `delivery` is the live row,
 * so after a 409 the reloaded version is used for the next try.
 */
export function DeliveryStepDialog({
  delivery,
  step,
  onClose,
}: {
  delivery: StaffDelivery | null;
  step: DeskStatus | null;
  onClose: () => void;
}) {
  const open = !!delivery && !!step;
  const mutation = useDeliveryStatus();
  const busy = mutation.isPending;
  return (
    <Dialog
      open={open}
      onClose={busy ? noop : onClose}
      dismissible={!busy}
      title={step ? COPY[step].title : 'Delivery'}
    >
      {delivery && step && (
        <StepForm
          key={`${delivery.id}-${step}`}
          delivery={delivery}
          step={step}
          mutation={mutation}
          onClose={onClose}
        />
      )}
    </Dialog>
  );
}

function StepForm({
  delivery,
  step,
  mutation,
  onClose,
}: {
  delivery: StaffDelivery;
  step: DeskStatus;
  mutation: ReturnType<typeof useDeliveryStatus>;
  onClose: () => void;
}) {
  const toast = useToast();
  const [values, setValues] = useState<Values>({
    courierName: delivery.courier?.name ?? '',
    courierPhone: delivery.courier?.phone ?? '',
    trackingUrl: delivery.courier?.trackingUrl ?? '',
    recipientName: '',
    idChecked: false,
    note: '',
    reason: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const copy = COPY[step];
  const allowed = (NEXT_STEPS[delivery.status] ?? []).includes(step);
  const set =
    (k: keyof Values) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setValues((v) => ({ ...v, [k]: event.target.value }));
  const apiField = (k: keyof Values) =>
    isApiError(mutation.error) ? mutation.error.fieldError(k) : undefined;

  const submit = () => {
    const next = validate(step, values);
    setErrors(next);
    if (Object.keys(next).length) return;
    setFailure(null);
    mutation
      .mutateAsync({
        id: delivery.id,
        body: body(step, delivery.version, values),
      })
      .then(() => {
        toast.show({ tone: 'success', title: copy.done });
        onClose();
      })
      .catch((err: unknown) => {
        const stale = isApiError(err) && STALE.includes(err.code);
        setFailure(
          `${errorBody(err) ?? 'Not saved.'}${stale ? ' The list has been reloaded.' : ''}`,
        );
      });
  };

  return (
    <form
      className={styles.form}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <p className={styles.hint}>
        {delivery.patient.fullName} · {delivery.prescriptionNumber ?? '-'} ·{' '}
        {delivery.address}
      </p>
      {!allowed && (
        <p className={styles.warn} role="status">
          This delivery has moved on and this step is no longer possible.
        </p>
      )}
      {step === 'out_for_delivery' && (
        <>
          <Field label="Courier name (optional)" error={errors.courierName}>
            <Input
              value={values.courierName}
              maxLength={80}
              onChange={set('courierName')}
            />
          </Field>
          <Field
            label="Courier phone (optional)"
            hint="Shown to the patient in the app."
            error={errors.courierPhone ?? apiField('courierPhone')}
          >
            <Input
              type="tel"
              value={values.courierPhone}
              maxLength={30}
              onChange={set('courierPhone')}
            />
          </Field>
          <Field
            label="Tracking link (optional)"
            hint="The courier's https link, shown to the patient."
            error={errors.trackingUrl ?? apiField('trackingUrl')}
          >
            <Input
              type="url"
              value={values.trackingUrl}
              maxLength={500}
              onChange={set('trackingUrl')}
            />
          </Field>
        </>
      )}
      {step === 'delivered' && (
        <>
          <Field
            label="Received by"
            error={errors.recipientName ?? apiField('recipientName')}
          >
            <Input
              value={values.recipientName}
              maxLength={120}
              onChange={set('recipientName')}
            />
          </Field>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={values.idChecked}
              onChange={(event) =>
                setValues((v) => ({ ...v, idChecked: event.target.checked }))
              }
            />
            The courier checked the recipient&apos;s IC
          </label>
          <Field
            label="Proof note or reference (optional)"
            hint="E.g. the courier's proof reference or “left with the guard”. Staff only, never shown to the patient."
            error={errors.note}
          >
            <textarea
              className={styles.textarea}
              rows={3}
              maxLength={300}
              value={values.note}
              onChange={set('note')}
            />
          </Field>
        </>
      )}
      {(step === 'failed' || step === 'cancelled') && (
        <Field
          label="Reason"
          hint="Staff only, never shown to the patient."
          error={errors.reason ?? apiField('reason')}
        >
          <textarea
            className={styles.textarea}
            rows={3}
            maxLength={300}
            value={values.reason}
            onChange={set('reason')}
          />
        </Field>
      )}
      {step === 'returned' && (
        <p className={styles.hint}>
          The medicines are back at the clinic and the patient collects them at
          the counter (they are told in the app). An unpaid delivery fee bill is
          voided; a paid one is flagged for a refund in Billing.
        </p>
      )}
      {step === 'cancelled' && (
        <p className={styles.hint}>
          The patient is told in the app. An unpaid delivery fee bill is voided;
          if the fee was paid, refund it in Billing first.
        </p>
      )}
      {failure && (
        <p className={styles.error} role="alert">
          {failure}
        </p>
      )}
      <div className={styles.formActions}>
        <Button onClick={onClose} disabled={mutation.isPending}>
          Back
        </Button>
        <Button
          type="submit"
          variant={copy.danger ? 'danger' : 'primary'}
          loading={mutation.isPending}
          disabled={!allowed}
        >
          {copy.submit}
        </Button>
      </div>
    </form>
  );
}
